import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Contract {
  id: string
  customer: string
  type: string
  value: number
  start_date: string
  end_date: string
  renewal_probability: number
  status: string
  auto_renew: boolean
}

interface Renewal {
  id: string
  customer: string
  current_value: number
  renewal_value: number
  renewal_date: string
  probability: number
  risk_factors: string[]
  recommended_action: string
}

interface TermsAnalysis {
  id: string
  term: string
  standard_clause: string
  contracts_with_standard: number
  contracts_with_custom: number
  risk_level: string
  negotiation_frequency: number
}

interface Statistics {
  active_contracts: number
  total_contract_value: number
  avg_contract_value: number
  renewal_rate: number
  upcoming_renewals_30d: number
  at_risk_value: number
}

export default function ContractIntelligencePage() {
  const [contracts, setContracts] = useState<Contract[]>([])
  const [renewals, setRenewals] = useState<Renewal[]>([])
  const [terms, setTerms] = useState<TermsAnalysis[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [contractsRes, renewalsRes, termsRes, statsRes] = await Promise.all([
        fetch('/api/contractintelligence/contracts'),
        fetch('/api/contractintelligence/renewals'),
        fetch('/api/contractintelligence/terms'),
        fetch('/api/contractintelligence/statistics')
      ])
      if (contractsRes.ok) setContracts(await contractsRes.json())
      if (renewalsRes.ok) setRenewals(await renewalsRes.json())
      if (termsRes.ok) setTerms(await termsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching contract intelligence data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'active' || status === 'renewed') return 'bg-green-100 text-green-800'
    if (status === 'expiring') return 'bg-yellow-100 text-yellow-800'
    if (status === 'churned') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getRiskBadge = (risk: string) => {
    if (risk === 'low') return 'bg-green-100 text-green-800'
    if (risk === 'medium') return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Contract Intelligence</h1>
          <p className="mt-2 text-gray-600">Phase 239 - Contracts, renewals, and terms analysis</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Contracts</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.active_contracts}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Value</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.total_contract_value)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Value</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.avg_contract_value)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Renewal Rate</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.renewal_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Renewals (30d)</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.upcoming_renewals_30d}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">At Risk</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(statistics.at_risk_value)}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Contracts</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Renewal Prob</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Auto-Renew</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {contracts.map((contract) => (
                  <tr key={contract.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{contract.customer}</td>
                    <td className="px-6 py-4 text-gray-500">{contract.type}</td>
                    <td className="px-6 py-4 text-blue-600 font-medium">{formatCurrency(contract.value)}</td>
                    <td className="px-6 py-4 text-gray-500 text-sm">{contract.start_date} - {contract.end_date}</td>
                    <td className={`px-6 py-4 ${contract.renewal_probability >= 70 ? 'text-green-600' : contract.renewal_probability >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>{contract.renewal_probability}%</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(contract.status)}`}>{contract.status}</span>
                    </td>
                    <td className="px-6 py-4">{contract.auto_renew ? <span className="text-green-600">Yes</span> : <span className="text-gray-400">No</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Upcoming Renewals</h2>
            </div>
            <div className="p-6 space-y-4">
              {renewals.map((renewal) => (
                <div key={renewal.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{renewal.customer}</span>
                    <span className={`text-lg font-bold ${renewal.probability >= 70 ? 'text-green-600' : 'text-yellow-600'}`}>{renewal.probability}%</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>Current: {formatCurrency(renewal.current_value)}</span>
                    <span>Renewal: {formatCurrency(renewal.renewal_value)}</span>
                    <span>Date: {renewal.renewal_date}</span>
                  </div>
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">Risk Factors:</p>
                    <div className="flex flex-wrap gap-1">
                      {renewal.risk_factors.map((factor, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">{factor}</span>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-purple-600">{renewal.recommended_action}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Terms Analysis</h2>
            </div>
            <div className="p-6 space-y-4">
              {terms.map((term) => (
                <div key={term.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{term.term}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getRiskBadge(term.risk_level)}`}>{term.risk_level} risk</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">Standard: {term.standard_clause}</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Standard</p>
                      <p className="font-medium text-green-600">{term.contracts_with_standard}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Custom</p>
                      <p className="font-medium text-yellow-600">{term.contracts_with_custom}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Negotiated</p>
                      <p className="font-medium text-purple-600">{term.negotiation_frequency}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
